/**
 * Welcome Landing - Module 1: Animated welcome with role selection
 *
 * Features:
 * - Cinematic Lambda symbol entrance animation
 * - Personalized greeting
 * - Role selection (determines content branching)
 * - Journey preview with time estimates
 */

import { useState, useEffect } from 'react';
import { Sparkles, Users, Briefcase, FlaskConical, Eye, RotateCcw } from 'lucide-react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useOnboardingTelemetry } from '../../hooks/useOnboardingTelemetry';
import type { OnboardingRole } from '../../types/onboarding';
import {
  ROLE_DISPLAY_NAMES,
  ROLE_DESCRIPTIONS,
  ROLE_TIME_ESTIMATES,
} from '../../lib/onboarding/constants';
import { cn } from '../../lib/utils';

interface WelcomeLandingProps {
  onComplete: () => void;
}

const ROLE_ICONS = {
  internal_core: Users,
  research_collab: FlaskConical,
  contractor: Briefcase,
  guest: Eye,
} as const;

export default function WelcomeLanding({ onComplete }: WelcomeLandingProps) {
  const [showContent, setShowContent] = useState(false);
  const [selectedRole, setSelectedRole] = useState<OnboardingRole | null>(null);
  const [animationComplete, setAnimationComplete] = useState(false);

  const { setRole, setDisplayName, profileData, role: existingRole, reset, isComplete } = useOnboardingStore();
  const { trackEvent } = useOnboardingTelemetry();

  // Check for existing role (resuming)
  useEffect(() => {
    if (existingRole) {
      setSelectedRole(existingRole);
      setAnimationComplete(true);
      setShowContent(true);
    }
  }, [existingRole]);

  // Animate in content after logo
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 2000); // Match logo animation duration

    const animTimer = setTimeout(() => {
      setAnimationComplete(true);
    }, 2500);

    return () => {
      clearTimeout(timer);
      clearTimeout(animTimer);
    };
  }, []);

  const handleRoleSelect = (role: OnboardingRole) => {
    setSelectedRole(role);
    trackEvent('onboarding_started', { role });
  };

  const handleBeginJourney = () => {
    if (!selectedRole) return;

    setRole(selectedRole);
    onComplete();
  };

  const timeEstimate = selectedRole
    ? ROLE_TIME_ESTIMATES[selectedRole]
    : { min: 15, max: 30 };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 relative overflow-hidden">
      {/* Animated Stars Background */}
      <div className="absolute inset-0 pointer-events-none">
        <StarField />
      </div>

      {/* Skip animation button (power users) */}
      {!animationComplete && (
        <button
          onClick={() => {
            setShowContent(true);
            setAnimationComplete(true);
          }}
          className="absolute top-4 right-4 text-xs opacity-50 hover:opacity-100 transition-opacity"
          style={{ color: 'var(--color-text-muted)' }}
        >
          Skip animation
        </button>
      )}

      {/* Main Content */}
      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Animated Lambda Logo */}
        <div
          className={cn(
            'mb-8 transition-all duration-1000',
            showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-8'
          )}
        >
          <div
            className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4"
            style={{
              background: 'radial-gradient(circle, var(--color-accent-muted) 0%, transparent 70%)',
            }}
          >
            <span
              className="lambda-symbol text-7xl animate-pulse"
              style={{ color: 'var(--color-accent)' }}
            >
              Λ
            </span>
          </div>
          <h1 className="lambda-brand text-4xl tracking-wider mb-2">
            <span style={{ color: 'var(--color-text-primary)' }}>LUKH</span>
            <span style={{ color: 'var(--color-accent)' }}>Λ</span>
            <span style={{ color: 'var(--color-text-primary)' }}>S </span>
            <span style={{ color: 'var(--color-accent)' }}>Λ</span>
            <span style={{ color: 'var(--color-text-primary)' }}>I</span>
          </h1>
          <p
            className="text-lg"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Superior Consciousness Technology
          </p>
        </div>

        {/* Welcome Message */}
        <div
          className={cn(
            'mb-10 transition-all duration-700 delay-300',
            showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'
          )}
        >
          <h2
            className="text-2xl font-semibold mb-3"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Welcome{profileData.displayName ? `, ${profileData.displayName}` : ''}
          </h2>
          <p
            className="text-base leading-relaxed max-w-md mx-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            You're joining a team building the future of conscious artificial intelligence.
            Let's get you set up with everything you need.
          </p>
        </div>

        {/* Role Selection */}
        <div
          className={cn(
            'mb-8 transition-all duration-700 delay-500',
            showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'
          )}
        >
          <h3
            className="text-sm font-semibold uppercase tracking-wider mb-4"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Select Your Role
          </h3>
          <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
            {(Object.keys(ROLE_DISPLAY_NAMES) as OnboardingRole[]).map((role) => {
              const Icon = ROLE_ICONS[role];
              const isSelected = selectedRole === role;

              return (
                <button
                  key={role}
                  onClick={() => handleRoleSelect(role)}
                  className={cn(
                    'p-4 rounded-xl text-left transition-all duration-200',
                    'border-2 group'
                  )}
                  style={{
                    backgroundColor: isSelected
                      ? 'var(--color-accent-muted)'
                      : 'var(--color-bg-secondary)',
                    borderColor: isSelected
                      ? 'var(--color-accent)'
                      : 'var(--color-border)',
                  }}
                  aria-pressed={isSelected}
                  aria-label={`Select ${ROLE_DISPLAY_NAMES[role]} role`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor: isSelected
                          ? 'var(--color-accent)'
                          : 'var(--color-bg-tertiary)',
                      }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{
                          color: isSelected
                            ? 'var(--color-bg)'
                            : 'var(--color-text-secondary)',
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-semibold text-sm"
                        style={{
                          color: isSelected
                            ? 'var(--color-accent)'
                            : 'var(--color-text-primary)',
                        }}
                      >
                        {ROLE_DISPLAY_NAMES[role]}
                      </p>
                      <p
                        className="text-xs mt-0.5 line-clamp-2"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {ROLE_DESCRIPTIONS[role]}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Journey Preview & CTA */}
        <div
          className={cn(
            'transition-all duration-700 delay-700',
            showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'
          )}
        >
          {/* Time Estimate */}
          <div
            className="flex items-center justify-center gap-4 mb-6 text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <span>6 short steps</span>
            <span style={{ color: 'var(--color-border)' }}>|</span>
            <span>~{timeEstimate.min}-{timeEstimate.max} minutes</span>
            <span style={{ color: 'var(--color-border)' }}>|</span>
            <span>Resumable anytime</span>
          </div>

          {/* Begin Button */}
          <button
            onClick={handleBeginJourney}
            disabled={!selectedRole}
            className={cn(
              'inline-flex items-center gap-2 px-8 py-4 rounded-xl',
              'font-semibold text-lg transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'hover:scale-105 active:scale-100'
            )}
            style={{
              backgroundColor: selectedRole ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
              color: selectedRole ? 'var(--color-bg)' : 'var(--color-text-muted)',
            }}
          >
            <Sparkles className="w-5 h-5" />
            Begin Your Journey
          </button>

          {/* Progress Note */}
          <p
            className="mt-4 text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Your progress is saved automatically
          </p>
        </div>
      </div>

      {/* Dev Reset Button - visible when onboarding has been started */}
      {(existingRole || isComplete) && (
        <button
          onClick={() => {
            if (window.confirm('Reset onboarding progress? This will clear all saved data and start fresh.')) {
              reset();
              setSelectedRole(null);
              window.location.reload();
            }
          }}
          className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all opacity-40 hover:opacity-100"
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-muted)',
            border: '1px solid var(--color-border)',
          }}
          title="Reset onboarding progress (dev testing)"
        >
          <RotateCcw className="w-3 h-3" />
          Reset Onboarding
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Star Field Animation
// ============================================================================

function StarField() {
  return (
    <div className="absolute inset-0">
      {/* Static stars */}
      {Array.from({ length: 50 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-twinkle"
          style={{
            width: Math.random() * 2 + 1 + 'px',
            height: Math.random() * 2 + 1 + 'px',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            backgroundColor: 'var(--color-text-muted)',
            opacity: Math.random() * 0.5 + 0.2,
            animationDelay: Math.random() * 3 + 's',
            animationDuration: Math.random() * 2 + 2 + 's',
          }}
        />
      ))}
      {/* Accent stars */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={`accent-${i}`}
          className="absolute rounded-full"
          style={{
            width: '3px',
            height: '3px',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            backgroundColor: 'var(--color-accent)',
            opacity: 0.6,
            boxShadow: '0 0 10px var(--color-accent)',
          }}
        />
      ))}
    </div>
  );
}
